import { Nullable } from "@core/domain/types/nullable.type";
import type { Undefinable } from "@core/domain/types/undefinable.type";
import { LoadLayoutStore } from "@core/presentacion/component/feedback/load-layout/load-layout.store";
import { ToastManagerStore } from "@core/presentacion/component/feedback/toast-manager/toast-manager.store";
import { BaseViewModel } from "@core/presentacion/view-model/base/base.viewmodel";
import { CreateBudgetCategoryGroup } from "@project/domain/models/budget/budget-category/groups/create-budget-category-group.model";
import { EditBudgetCategoryGroup } from "@project/domain/models/budget/budget-category/groups/edit-budget-category-group.model";
import {
    BudgetCategoryGroup,
    BudgetCategoryGroups,
} from "@project/domain/models/budget/financial-entity-budget.model";
import { CreateBudgetCategoryGroupUseCase } from "@project/domain/usecases/budget/categories/groups/create-category-group.usecase";
import { DeleteBudgetCategoryGroupUseCase } from "@project/domain/usecases/budget/categories/groups/delete-category-group.usecase";
import { EditCategoryGroupUseCase } from "@project/domain/usecases/budget/categories/groups/edit-category-group.usecase";
import { GetAllCategoryGroupsByCategoryIdUseCase } from "@project/domain/usecases/budget/categories/groups/get-all-category-groups-by-category-id.usecase";
import {
    ExpenseSubtypeOption,
    GetCategoryGroupExpenseSubtypesUseCase,
} from "@project/domain/usecases/budget/categories/groups/get-category-group-expense-subtypes.usecase";
import {
    ExpenseTypeOption,
    GetCategoryGroupExpenseTypesUseCase,
} from "@project/domain/usecases/budget/categories/groups/get-category-group-expense-types.usecase";
import { inject, injectable } from "inversify";
import { makeObservable, observable, runInAction } from "mobx";

@injectable()
export class BudgetCategoryGroupsViewModel extends BaseViewModel {
    _categoryId: Nullable<number> = null;

    @observable
    groups: BudgetCategoryGroups = new BudgetCategoryGroups([], 0);

    @observable
    openAddModal: boolean = false;

    @observable
    openEditModal: boolean = false;

    @observable
    groupToEdit: Undefinable<BudgetCategoryGroup>;

    @observable
    expenseTypes: ExpenseTypeOption[] = [];

    @observable
    expenseSubtypes: ExpenseSubtypeOption[] = [];

    // Cache of subtype key -> label to render labels in read-only view even when options aren't loaded
    @observable
    subtypeLabelMap: Map<string, string> = new Map();

    constructor(
        @inject(GetAllCategoryGroupsByCategoryIdUseCase)
        private readonly getAllCategoryGroupsByCategoryIdUseCase: GetAllCategoryGroupsByCategoryIdUseCase,
        @inject(DeleteBudgetCategoryGroupUseCase)
        private readonly deleteCategoryGroupUseCase: DeleteBudgetCategoryGroupUseCase,
        @inject(CreateBudgetCategoryGroupUseCase)
        private readonly createCategoryGroupUseCase: CreateBudgetCategoryGroupUseCase,
        @inject(EditCategoryGroupUseCase)
        private readonly editCategoryGroupUseCase: EditCategoryGroupUseCase,
        @inject(GetCategoryGroupExpenseTypesUseCase)
        private readonly getCategoryGroupExpenseTypesUseCase: GetCategoryGroupExpenseTypesUseCase,
        @inject(GetCategoryGroupExpenseSubtypesUseCase)
        private readonly getCategoryGroupExpenseSubtypesUseCase: GetCategoryGroupExpenseSubtypesUseCase,
    ) {
        super();
        makeObservable(this);
    }
    override async didMount(): Promise<void> {
        await super.didMount();
        await this.initViewData();
    }
    async initViewData(): Promise<void> {
        await this.getGroups();
        await this.getExpenseTypes();
    }

    async getExpenseTypes(): Promise<void> {
        const expenseTypes =
            await this.getCategoryGroupExpenseTypesUseCase.execute();

        runInAction(() => {
            // Filter out NONE type
            this.expenseTypes = expenseTypes.filter(
                (type) => type.value !== "NONE",
            );
        });
    }

    async getExpenseSubtypes(typeId?: string): Promise<void> {
        runInAction(() => {
            this.expenseSubtypes = [];
        });
        if (!typeId) return;
        const subtypes =
            await this.getCategoryGroupExpenseSubtypesUseCase.execute(typeId);
        runInAction(() => {
            this.expenseSubtypes = subtypes;
            // Update cache map with fetched labels
            subtypes.forEach((st) =>
                this.subtypeLabelMap.set(st.value, st.label),
            );
        });
    }
    async getGroups(): Promise<void> {
        if (!this._categoryId) {
            return;
        }
        const groups =
            await this.getAllCategoryGroupsByCategoryIdUseCase.execute(
                this._categoryId,
            );
        runInAction(() => {
            this.groups = groups;
        });
        // Preload subtype labels for all types present to show readable labels in detail view
        await this.prefetchSubtypeLabelsForCurrentGroups();
    }
    handleOpenAddModal = (): void => {
        runInAction(() => {
            this.openAddModal = true;
            this.expenseSubtypes = [];
        });
    };
    handleCloseAddModal = (): void => {
        runInAction(() => {
            this.openAddModal = false;
        });
    };

    addGroup = async (values: CreateBudgetCategoryGroup): Promise<void> => {
        if (!this._categoryId) return;

        LoadLayoutStore.start();
        const groups = await this.createCategoryGroupUseCase.execute(
            new CreateBudgetCategoryGroup(
                values.name,
                this._categoryId,
                values.expense_type_mapping,
                values.expense_subtype_mapping,
            ),
        );
        if (groups) {
            ToastManagerStore.success();
        }

        await this.getGroups();
        LoadLayoutStore.finish();
        this.handleCloseAddModal();
    };

    handleOpenEditModal = (group: BudgetCategoryGroup): void => {
        runInAction(() => {
            this.openEditModal = true;
            this.groupToEdit = group;
        });
        // Load subtypes for current type when editing
        void this.getExpenseSubtypes(group.expense_type_mapping);
    };

    handleCloseEditModal = (): void => {
        runInAction(() => {
            this.openEditModal = false;
            this.groupToEdit = undefined;
            this.expenseSubtypes = [];
        });
    };

    editGroup = async (values: EditBudgetCategoryGroup): Promise<void> => {
        if (!this._categoryId) return;
        if (!this.groupToEdit) return;

        LoadLayoutStore.start();

        const result = await this.editCategoryGroupUseCase.execute(
            new EditBudgetCategoryGroup(
                values.id,
                values.name,
                values.category,
                values.expense_type_mapping,
                values.expense_subtype_mapping,
            ),
        );

        if (result) {
            ToastManagerStore.success();
        }

        await this.getGroups();
        LoadLayoutStore.finish();
        this.handleCloseEditModal();
    };

    deleteGroup = async (id: number): Promise<void> => {
        LoadLayoutStore.start();
        const result = await this.deleteCategoryGroupUseCase.execute(id);
        if (result) {
            await this.getGroups();
            ToastManagerStore.success();
        }
        LoadLayoutStore.finish();
    };

    private async prefetchSubtypeLabelsForCurrentGroups(): Promise<void> {
        const types = Array.from(
            new Set(
                (this.groups?.budgetCategoryGroups ?? [])
                    .map((g) => g.expense_type_mapping)
                    .filter((v): v is string => Boolean(v)),
            ),
        );
        if (types.length === 0) return;
        try {
            const all = await Promise.all(
                types.map((typeId) =>
                    this.getCategoryGroupExpenseSubtypesUseCase.execute(typeId),
                ),
            );
            runInAction(() => {
                all.forEach((list) =>
                    list.forEach((st) =>
                        this.subtypeLabelMap.set(st.value, st.label),
                    ),
                );
            });
        } catch (_) {
            // ignore silent failures; UI will fallback to keys if needed
        }
    }
}
